import { FC, ReactNode, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useRbacPermission } from '../../hooks/useRbacPermissionHook';
import { useRbacState } from '../../context/rbac.context';
import { hasAnySubmoduleReadPermission } from '../../utils/rbacHelpers';
import { getSafeLandingPath } from '../../utils/rbacLandingPaths';
import { isGuardianUserRole } from '../../utils/guardianAuth';
import { pages } from '../../utils/common';
import { useTranslation } from '../../hooks/useTranslationHook';

interface ProtectedRouteProps {
  module: string;
  submodule?: string;
  children: ReactNode;
  redirectTo?: string;
  requireWrite?: boolean;
  checkAnySubmodule?: boolean;
}

/**
 * Component that protects routes based on RBAC permissions
 * @param module - Module name (e.g., 'students', 'staff')
 * @param submodule - Submodule name (e.g., 'studentList', 'attendance'). Optional if checkAnySubmodule is true
 * @param children - Content to render if permission is granted
 * @param redirectTo - Optional redirect path if permission is denied (defaults to profile / safe landing)
 * @param requireWrite - If true, requires write permission instead of read (default: false)
 * @param checkAnySubmodule - If true, checks if user has read permission for any submodule in the module (default: false)
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  module,
  submodule,
  children,
  redirectTo,
  requireWrite = false,
  checkAnySubmodule = false,
}) => {
  const { rbac, isLoading, errorMessage } = useRbacState();
  const location = useLocation();
  const { canRead, canWrite, hasAny } = useRbacPermission(
    module,
    submodule || ''
  );
  const { translateText } = useTranslation();

  const isGuardianSurface = location.pathname.startsWith(pages.guardian.path);

  const defaultDenyRedirect = useMemo(
    () =>
      getSafeLandingPath(rbac as Record<string, unknown> | null, {
        isGuardian: false,
        currentPathname: location.pathname,
      }),
    [rbac, location.pathname]
  );

  if (isGuardianSurface) {
    if (!isGuardianUserRole()) {
      return <Navigate to={pages.dashboard.path} replace />;
    }
    return <>{children}</>;
  }

  // Handle loading state with proper UI
  if (isLoading) {
    return (
      <Box
        data-testid="rbac-loading"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: { xs: 1, sm: 2 },
          p: { xs: 2, sm: 4 },
        }}
      >
        <CircularProgress />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center',
          }}
        >
          {translateText('rbac.errors.loading')}
        </Typography>
      </Box>
    );
  }

  // Real failure: API error set errorMessage. Avoid `|| !rbac` — stale errorMessage can
  // linger one frame after RBAC loads; v5 isLoading is false while pending but fetch not started.
  if (!rbac && errorMessage) {
    return (
      <Box
        data-testid="rbac-error"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: { xs: 2, sm: 4 },
        }}
      >
        <Alert
          severity="error"
          sx={{
            maxWidth: { xs: '100%', sm: 500 },
            width: '100%',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
            }}
          >
            {translateText('rbac.errors.loadError')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
            }}
          >
            {errorMessage}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!rbac) {
    return (
      <Box
        data-testid="rbac-loading-fallback"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: { xs: 1, sm: 2 },
          p: { xs: 2, sm: 4 },
        }}
      >
        <CircularProgress />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center',
          }}
        >
          {translateText('rbac.errors.loading')}
        </Typography>
      </Box>
    );
  }

  let hasRequiredPermission = false;

  if (checkAnySubmodule) {
    hasRequiredPermission = hasAnySubmoduleReadPermission(rbac, module);
  } else if (submodule) {
    hasRequiredPermission = requireWrite ? canWrite : canRead;
  }

  if (!hasRequiredPermission && !hasAny && !checkAnySubmodule) {
    return <Navigate to={redirectTo ?? defaultDenyRedirect} replace />;
  }

  if (checkAnySubmodule && !hasRequiredPermission) {
    return <Navigate to={redirectTo ?? defaultDenyRedirect} replace />;
  }

  return <>{children}</>;
};
